import type { ConfigService } from '../App/services/mx-config-service';
import type { I18n } from '../i18n/mx-internationalization';
import type { EventService } from '../App/services/event-service';
import type { Transition } from '@uirouter/angularjs';
import type { IRootScopeService } from 'angular';
import type { ShellAppInitializerService } from './Pandora/services/shell-app.initializer.service';
import { ContentWidgetDialogAction } from './PandoraSolutionBuilderAgent/model/enumerations';
import type { ActionProviderService } from './Pandora/services/mx-action-provider-service';
import type { DialogOpenerService } from './PandoraSolutionBuilderAgent/services/dialog-opener-service';
import type { SidePanelService } from './Pandora/services/mx-side-panel-service';
import type { WidgetManager } from './PandoraSolutionBuilderAgent/services/widget-manager';
import type { DeepLinkingService } from './PandoraSolutionBuilderAgent/services/deep-linking-service';
import type { DataService } from './PandoraSolutionBuilderAgent/services/data-service';
import type { FavouritesService } from './PandoraSolutionBuilderAgent/components/mx-left-side-panel/mx-favourites-items-panel/mx-favourites-items-service';
import type { RecentService } from './PandoraSolutionBuilderAgent/components/mx-left-side-panel/mx-recent-items-panel/mx-recent-items-service';
import type { MxShellWidgetsPanelService } from './PandoraSolutionBuilderAgent/components/mx-left-side-panel/mx-widgets-panel/mx-widgets-panel-service';
import type { EmbeddedViewService } from './PandoraSolutionBuilderAgent/services/embedded-view-service';
import type { MxNavigationService } from './Navigation/mx-navigation-service';
import type { RoutingService } from '../App/services/mx-routing-service';
import type { MxApplicationService } from '../App/services/mx-application-service';
import type { MxShellLeftSidebarService } from './Pandora/components/mx-shell-left-sidebar/mx-shell-left-sidebar-service';
import type { PushNotificationsService } from './PandoraSolutionBuilderAgent/modules/PushNotifications/push-notifications.service';
import type { SearchProviderService } from './Pandora/services/mx-search-provider-service';
import type { FeedbackProviderService } from './Pandora/services/mx-feedback-provider-service';
import type { DialogFactory } from './PandoraSolutionBuilderAgent/modules/DialogPage/dialog-factory';
import { AgentTelemetryService } from './PandoraSolutionBuilderAgent/services/agent-telemetry-service';
import { EventCodes } from '../App/model/event-codes';

export class AgentAppInitializerService {
	public static $inject = [
		'$rootScope',
		'mx.shell.Config',
		'mx.shell.SidePanelService',
		'mx.internationalization',
		'mx.shell.EventService',
		'mx.SolutionBuilderAgent.DialogOpenerService',
		'mx.SolutionBuilderAgent.WidgetManager',
		'mx.SolutionBuilderAgent.DeepLinkingService',
		'mx.SolutionBuilderAgent.FavouritesService',
		'mx.SolutionBuilderAgent.RecentService',
		'mx.SolutionBuilderAgent.WidgetsPanelService',
		'mx.SolutionBuilderAgent.DataService',
		'mx.SolutionBuilderAgent.EmbeddedViewService',
		'mx.shell.NavigationService',
		'mx.shell.ToolbarManager',
		'mx.shell.RoutingService',
		'mx.shell.AppsService',
		'mx.shell.ApplicationProviderService',
		'mx.SolutionBuilderAgent.ContentWidgetService',
		'mx.sb.DialogView.DialogFactory',
		'mx.SolutionBuilderAgent.WizardService',
		'mx.shell.FeedbackProviderService',
		'mx.sb.PushNotifications.NotificationsService',
		'mx.shell.SearchProviderService',
		'mx.shell.ActionProviderService',
		'mx.shell.LeftSidebarService',
		'mx.shell.ShellAppInitializerService',
		'mx.SolutionBuilderAgent.AgentTelemetryService',
	];
	constructor(
		private readonly $rootScope: IRootScopeService,
		private readonly configService: ConfigService,
		private readonly sidePanelService: ReturnType<typeof SidePanelService>,
		private readonly i18n: I18n,
		private readonly eventService: EventService,
		private readonly dialogOpenerService: ReturnType<typeof DialogOpenerService>,
		private readonly widgetManager: WidgetManager,
		private readonly deepLinkingService: DeepLinkingService,
		private readonly favouritesService: ReturnType<typeof FavouritesService>,
		private readonly recentService: ReturnType<typeof RecentService>,
		private readonly widgetsPanelService: MxShellWidgetsPanelService,
		private readonly dataService: ReturnType<typeof DataService>,
		private readonly embeddedViewService: EmbeddedViewService,
		private readonly navigationService: MxNavigationService,
		private readonly toolbarManager: any,
		private readonly routingService: RoutingService,
		private readonly appsService: MxApplicationService,
		private readonly applicationProviderService: any,
		private readonly contentWidgetService: any,
		private readonly dialogFactory: DialogFactory,
		private readonly wizardService: any,
		private readonly feedbackProviderService: FeedbackProviderService,
		private readonly pushService: PushNotificationsService,
		private readonly searchService: ReturnType<typeof SearchProviderService>,
		private readonly actionProviderService: ReturnType<typeof ActionProviderService>,
		private readonly leftSidebarService: ReturnType<typeof MxShellLeftSidebarService>,
		private readonly shellAppInitializerService: ShellAppInitializerService,
		private readonly agentTelemetryService: AgentTelemetryService
	) {}

	public async initAgentApp() {
		await this.shellAppInitializerService.initShellApp({
			applicationProvider: this.applicationProviderService,
			actionProvider: this.actionProviderService,
		});

		const navigationItems = await this.navigationService.get();
		this.searchService.setContextsFromNavigationItems(navigationItems);

		this.initActionCustomize();

		await this.initToolbar();

		this.feedbackProviderService.isEnabled().then((enabled: boolean) => {
			this.feedbackProviderService.FeedbackEnabled = enabled;
			this.feedbackProviderService.isFeedbackVisible = this.feedbackProviderService.FeedbackEnabled;
		});

		await this.registerSettingsNavigation();

		this.favouritesService.init();
		this.recentService.init();
		this.widgetsPanelService.init();
		this.pushService.init();
		this.agentTelemetryService.init();

		const isDevMode = this.configService.get('mode') === 'dev';
		this.runDataModelProfiler(isDevMode);
		this.runPerformanceProfiler(isDevMode);

		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		window.mx.Data = this.dataService;

		const state = await this.initAgentRouter();

		this.processViewOptions();

		return state;
	}

	private async initToolbar() {
		const app = await this.appsService.getCurrentApplication();

		this.toolbarManager.init();
		this.toolbarManager.setShowMenuButtonVisibility(true);
		this.toolbarManager.setToolbarVisibility(true);
		this.toolbarManager.setSearchVisibility(true);

		if (app.simpleNavigation) {
			this.toolbarManager.setBreadcrumbsVisibility(false);
			this.toolbarManager.setNavigationVisibility(true);
		} else {
			this.toolbarManager.setBreadcrumbsVisibility(true);
			this.toolbarManager.setNavigationVisibility(false);

			this.leftSidebarService.addPanel({
				name: 'navigation',
				icon: 'list',
				title: this.i18n.get('shell.common.navigation'),
				order: 10,
				content: '<mx-shell-navigation-panel></mx-shell-navigation-panel>',
			});
		}
	}

	private initActionCustomize() {
		this.contentWidgetService
			.isCustomizable({ dialogType: ContentWidgetDialogAction.Preview })
			.then((isCustomisable: boolean) => isCustomisable && this.dialogFactory.previewUiActionGroup.push('is_configurable', 'is_customizable'));
		this.contentWidgetService
			.isCustomizable({ dialogType: ContentWidgetDialogAction.Edit })
			.then((isCustomisable: boolean) => isCustomisable && this.dialogFactory.dialogsActionGroup.push('is_configurable', 'is_customizable'));
		this.contentWidgetService
			.isCustomizable({ isWizard: true })
			.then((isCustomizable: boolean) => isCustomizable && this.wizardService.wizardUiActionGroup.push('is_configurable', 'is_customizable'));
	}

	private processViewOptions() {
		const viewOptions = this.deepLinkingService.parseViewOptionsFromUrl();

		if (viewOptions) {
			this.deepLinkingService.processViewOptions(viewOptions);
		}

		if (viewOptions && viewOptions.embedded) {
			this.embeddedViewService.activate();
		}
	}

	private async initAgentRouter() {
		const app = await this.appsService.getCurrentApplication();

		const defaultRouteNotSetState = {
			parent: 'shell',
			name: 'defaultRouteNotSet',
		};

		const shellState = {
			parent: 'app',
			url: '/app-:appString',
			name: 'shell',
			component: 'mxShell',
			params: {
				appString: app.name,
			},

			redirectTo: async function (transition: Transition) {
				const routingService = transition.injector().get('mx.shell.RoutingService');
				if (routingService.defaultRoute) {
					return transition.router.stateService.target(routingService.defaultRoute.name, routingService.defaultRoute.parameters);
				} else {
					return defaultRouteNotSetState.name;
				}
			},
		};

		this.routingService.initShellStates(shellState);

		return defaultRouteNotSetState;
	}

	private async registerSettingsNavigation() {
		const settingsType = 'SPSGlobalConfigurationType';

		if (await this.widgetManager.hasPreviews(settingsType)) {
			this.navigationService.openSettingHandler = () => {
				this.sidePanelService.handleItemSelectionOnMobile();
				this.dialogOpenerService.openPreview({
					_name: this.i18n.get('solutionBuilderAgent.common.settings'),
					_type: settingsType,
					_id: 'CC0EA0CD-A84A-487C-AE1D-A68EF33E7DE1',
				});
				this.eventService.dispatchEvent(EventCodes.TrackTelemetry, { eventName: `Navigation clicked`, sender: 'settings', senderTitle: 'Settings' });
			};
		}
	}

	private runDataModelProfiler(isDevMode: boolean) {
		const dmDebugIsOn = debugIsOn('mx.dataModel.debug');
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		window.mx.__mx$dataModelProfilingOn = dmDebugIsOn;

		if (isDevMode) {
			const concurrencyDebugIsOn = debugIsOn('mx.concurrency.debug');
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			window.mx.__mx$concurrencyDebugOn = concurrencyDebugIsOn;

			console.log(
				'Concurrency debug mode is ' +
					(concurrencyDebugIsOn ? 'ON. Run `mx._concurrencyDebugOff()` to disable it.' : 'OFF. Run `mx._concurrencyDebugOn()` to enable Concurrency debug for a day')
			);

			console.log(
				'Data model debug mode is ' +
					(dmDebugIsOn ? 'ON. Run `mx._dataModelDebugOff()` to disable it.' : 'OFF. Run `mx._dataModelDebugOn()` to enable DataModel debug for a day')
			);

			registerDebugMethods('_concurrencyDebugOn', '_concurrencyDebugOff', 'mx.concurrency.debug', '__mx$concurrencyDebugOn');
		}

		registerDebugMethods('_dataModelDebugOn', '_dataModelDebugOff', 'mx.dataModel.debug', '__mx$dataModelProfilingOn' + isDevMode ? '_PROD' : '_DEV');

		function debugIsOn(key: string) {
			const profileOnTime = window.localStorage && window.localStorage.getItem(key);
			return !!(profileOnTime && Date.now() - parseInt(profileOnTime) < 86400000);
		}

		function registerDebugMethods(onMethodName: string, offMethodName: string, key: string, debugVariableName: string) {
			window.mx = window.mx || {};

			function setDebugKey(key: string, value: number | null) {
				if (window.localStorage) {
					if (value !== null) {
						window.localStorage.setItem(key, value.toString());
					} else {
						window.localStorage.removeItem(key);
					}
				}
			}

			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			window.mx[offMethodName] = function () {
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				window.mx[debugVariableName] = false;
				setDebugKey(key, null);

				console.info(key + ' is disabled');
			};

			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			window.mx[onMethodName] = function () {
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				window.mx[debugVariableName] = true;
				setDebugKey(key, Date.now());

				console.info(key + ' is enabled for a day');
				window.location.reload();
			};
		}
	}

	private runPerformanceProfiler(isDevMode: boolean) {
		if (!isDevMode) {
			return;
		}

		// Performance debug. Lets remove it when performance becomes not so critical.
		const profileOnTime = window.localStorage && window.localStorage.getItem('mx.performance.debug');
		if (profileOnTime && Date.now() - parseInt(profileOnTime) < 86400000) {
			let _oldWatcherCounter = 0;
			this.$rootScope.$watch(() => {
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				window.mx.___mx$digestCounter = window.mx.___mx$digestCounter ? window.mx.___mx$digestCounter + 1 : 1;
				const $watchers = this._countWatchers();
				const wDelta = $watchers - _oldWatcherCounter;
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				console.log('mx-debug: $digest #' + window.mx.___mx$digestCounter, ' $watchers: ', $watchers, '(' + (wDelta > 0 ? '+' : '') + wDelta + ')');
				_oldWatcherCounter = $watchers;
			});
		} else {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			window.mx._performanceDebugOn = function () {
				if (window.localStorage) {
					window.localStorage.setItem('mx.performance.debug', Date.now().toString());
				}
				console.info('mx-performance-debug is enabled for a day');
				window.location.reload();
			};
			console.info('mx-debug: Run `mx._performanceDebugOn()` to enable mx-performance-debug for a day.');
		}
	}

	private _countWatchers() {
		const root = this.$rootScope;
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		let count = root.$$watchers ? root.$$watchers.length : 0; // Include the current scope
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const pendingChildHeads = [root.$$childHead];
		let currentScope;
		while (pendingChildHeads.length) {
			currentScope = pendingChildHeads.shift();
			while (currentScope) {
				count += currentScope.$$watchers ? currentScope.$$watchers.length : 0;
				pendingChildHeads.push(currentScope.$$childHead);
				currentScope = currentScope.$$nextSibling;
			}
		}
		return count;
	}
}
